import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Badge,
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  FileDownloadOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  PrintOutlined,
  ViewModuleOutlined,
  AssignmentIndOutlined,
  DoneAllOutlined,
} from "@mui/icons-material";
import ImageAPI from "../../../api/ImageAPI";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import PodAllocatedOrderListTable from "../../components/pod-order/PodAllocatedOrderListTable";
import PodOrderPrintOutwardDrawer from "../../components/pod-order/PodOrderPrintOutwardDrawer";
import PodOrderPrintInwardDrawer from "../../components/pod-order/PodOrderPrintInwardDrawer";
import ShipmentDetailsDrawer from "../shipments/ShipmentDetailsDrawer";
import {
  formatPodOrderBatchInventoryAllocateErrors,
  getPodOrderBatchInventoryAllocateFailures,
  parsePodOrderBatchInventoryAllocateResult,
  parsePodOrderBatchUnassignResult,
  PodOrderPrintOutwardConfirmPayload,
} from "../../../../../models/PodOrder/PodOrder";
import {
  PodOrderProgressStage,
} from "../../../../../models/PodOrder/PodOrderProgressStage";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  getPodPrintingTypeLabel,
  POD_PRINTING_TYPE_OPTIONS,
  toPodPrintingTypeFilterIdentifier,
} from "../../../../../models/PodOrder/PodPrintingType";

const PREVIOUSLY_ALLOCATED_OPTIONS: FilterValue[] = [
  { id: "true", value: "Yes" },
  { id: "false", value: "No" },
];

const POD_ORDER_LIST_DEFAULT_TAB_ID = "allocated-orders";

const PodAllocatedOrderList: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { podOrderStore, userStore, orderStore, warehouseStore } = useStores();
  const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const shipmentSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const { showError, showSuccess, showWarning } = useNotification();
  const {
    params,
    selectedOrders,
    selectedShipmentId,
    bulkWarehouseId,
    bulkWarehouseError,
    bulkWarehouseSaving,
    printingTypeValue,
    printingTypeError,
    printingTypeSaving,
  } = podOrderStore;
  const [warehousePopupOpen, setWarehousePopupOpen] = useState(false);
  const [printingTypePopupOpen, setPrintingTypePopupOpen] = useState(false);
  // Allocated lines cannot change warehouse; they must be moved to Unassigned first.
  const isAllocatedLine = (order: { approved?: number | null }) =>
    Number(order.approved) === 1;
  const describeLine = (order: { order_id: number; product_name?: string | null }) =>
    `Order #${order.order_id}${order.product_name?.trim() ? ` · ${order.product_name.trim()}` : ""}`;
  const podOrderStatusTabs = useMemo(
    () => [
      {
        id: "all",
        label: "In-Process Orders",
        status: "",
        Icon: ViewModuleOutlined,
        iconColor: theme.palette.customColors?.greyTextColor,
        action: null,
        selectionEnabled: false,
      },
      {
        id: "unassigned",
        label: "UNASSIGNED",
        status: PodOrderProgressStage.UNASSIGNED,
        Icon: AssignmentIndOutlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: {
          label: "Allocate",
          targetStatus: PodOrderProgressStage.INVENTORY_ALLOCATED,
        },
        secondaryAction: {
          label: "Move to Unassigned",
        },
        selectionEnabled: true,
      },
      {
        id: "allocated-orders",
        label: "Allocated orders",
        status: PodOrderProgressStage.INVENTORY_ALLOCATED,
        Icon: Inventory2Outlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: {
          label: "Print Outward",
          targetStatus: PodOrderProgressStage.OUT_FOR_PRINTING,
        },
        secondaryAction: {
          label: "Move to Unassigned",
        },
        selectionEnabled: true,
      },
      {
        id: "out-for-printing",
        label: "Print Outward",
        status: PodOrderProgressStage.OUT_FOR_PRINTING,
        Icon: PrintOutlined,
        iconColor: theme.palette.customColors?.pinkDark,
        action: {
          label: "Print Inward",
          targetStatus: PodOrderProgressStage.PARTIALLY_PRINT_INWARD,
        },
        selectionEnabled: true,
      },
      {
        id: "printing-completed",
        label: "Partially Print Inward",
        status: PodOrderProgressStage.PARTIALLY_PRINT_INWARD,
        Icon: FactCheckOutlined,
        iconColor: theme.palette.customColors?.greenDark,
        action: null,
        selectionEnabled: false,
      },
      {
        id: "shipment-created",
        label: "Shipment Created",
        status: PodOrderProgressStage.SHIPMENT_CREATED,
        Icon: LocalShippingOutlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: null,
        selectionEnabled: false,
      },
      {
        id: "shipped",
        label: "Shipped",
        status: PodOrderProgressStage.SHIPMENT_SHIPPED,
        Icon: DoneAllOutlined,
        iconColor: theme.palette.customColors?.greenDark,
        action: null,
        selectionEnabled: false,
      },
    ],
    [theme],
  );
  const defaultTab =
    podOrderStatusTabs.find(tab => tab.id === POD_ORDER_LIST_DEFAULT_TAB_ID) ??
    podOrderStatusTabs[1];
  const activeTab =
    podOrderStatusTabs.find(tab => tab.id === params.tab_id) ?? defaultTab;

  const listQueryKey = [
    params.page,
    params.limit,
    params.tab_id,
    params.status,
    params.sku,
    params.customer_name,
    params.start_date,
    params.end_date,
    params.dispatch_start_date,
    params.dispatch_end_date,
    params.sort_by,
    params.sort_type,
    (params.order_ids ?? []).join(","),
    (params.shipment_ids ?? []).join(","),
    params.printing_type ?? "",
    params.previously_allocated ?? "",
  ].join("|");

  const handleOrderSearch = (searchText?: string) => {
    if (orderSearchTimerRef.current) clearTimeout(orderSearchTimerRef.current);
    if (searchText && searchText.length > 0) {
      orderSearchTimerRef.current = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch(() => { });
      }, 500);
    }
  };

  const handleShipmentSearch = (searchText?: string) => {
    if (shipmentSearchTimerRef.current) clearTimeout(shipmentSearchTimerRef.current);
    shipmentSearchTimerRef.current = setTimeout(() => {
      podOrderStore.searchShipmentIds(searchText);
    }, 300);
  };


  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabIdParam = searchParams.get("tab_id");
    const resolvedTabId =
      tabIdParam && podOrderStatusTabs.some(tab => tab.id === tabIdParam)
        ? tabIdParam
        : POD_ORDER_LIST_DEFAULT_TAB_ID;
    const selectedTab =
      podOrderStatusTabs.find(tab => tab.id === resolvedTabId) ?? defaultTab;
    podOrderStore.initializeParamsFromSearch(
      searchParams,
      selectedTab.id,
      selectedTab.status,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (activeTab.id !== "unassigned") return;
    warehouseStore.searchWarehouse().catch(() => {
      showError("Error", "Failed to load warehouses.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab.id]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_ALLOCATED_POD_ORDER])) return;
    podOrderStore
      .getPodOrders()
      .then(res => {
        if (!res && podOrderStore.error) showError("Error", podOrderStore.error);
      })
      .catch(() => {
        showError("Error", "Error fetching POD Orders");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryKey]);


  useEffect(() => {
    if (podOrderStore.params !== params) return;
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set("page", params.page.toString());
    if (params.limit) searchParams.set("limit", params.limit.toString());
    if (params.tab_id) searchParams.set("tab_id", params.tab_id);
    if (params.status) searchParams.set("status", params.status);
    if (params.sku) searchParams.set("sku", params.sku);
    if (params.customer_name)
      searchParams.set("customer_name", params.customer_name);
    if (params.start_date) searchParams.set("start_date", params.start_date);
    if (params.end_date) searchParams.set("end_date", params.end_date);
    if (params.dispatch_start_date)
      searchParams.set("dispatch_start_date", params.dispatch_start_date);
    if (params.dispatch_end_date)
      searchParams.set("dispatch_end_date", params.dispatch_end_date);
    if (params.sort_by) searchParams.set("sort_by", params.sort_by);
    if (params.sort_type) searchParams.set("sort_type", params.sort_type);
    params.order_ids?.forEach(id => searchParams.append("order_id", id.toString()));
    params.shipment_ids?.forEach(id =>
      searchParams.append("shipment_id", id.toString()),
    );
    if (params.printing_type) {
      searchParams.set("printing_types", params.printing_type);
    }
    if (activeTab.id === "unassigned" && params.previously_allocated) {
      searchParams.set("previously_allocated", params.previously_allocated);
    }

    const nextSearch = searchParams.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate({ search: nextSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryKey, navigate]);

  // Switch tab after a successful action (page reset); refetch when already on that tab.
  const goToTab = (
    tabId: string,
  ): ReturnType<typeof podOrderStore.getPodOrders> => {
    const tab = podOrderStatusTabs.find(t => t.id === tabId);
    if (!tab || podOrderStore.params.tab_id === tab.id) {
      return podOrderStore.getPodOrders();
    }
    podOrderStore.setParams({ tab_id: tab.id, status: tab.status, page: 1 });
    return Promise.resolve(undefined);
  };

  const handleStatusTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    const selectedTab =
      podOrderStatusTabs.find(tab => tab.id === newValue) ?? podOrderStatusTabs[0];
    podOrderStore.setParams({
      tab_id: selectedTab.id,
      status: selectedTab.status,
      page: 1,
    });
  };

  const handleExport = () => {
    if (podOrderStore.podOrderListingResponse.total_count === 0) {
      showError("Error", "No data to export.");
      return;
    }

    podOrderStore.bulkDownloadPodOrders(params).then(res => {
      if (!res) {
        showError(
          "Error",
          podOrderStore.error || "Failed to start POD orders export.",
        );
        return;
      }
      if (!res.success) {
        showError(
          "Error",
          res.message || "Failed to start POD orders export.",
        );
        return;
      }
      showSuccess(
        res.message ||
          "POD orders download job started. You will receive an email with the CSV file.",
      );
    });
  };

  const handleAllocateSelected = () => {
    podOrderStore.setConfirmLoading(true);
    podOrderStore
      .batchInventoryAllocateSelected()
      .then(res => {
        if (res == null) {
          showError("Error", "No rows selected.");
          return;
        }
        if (!res.success) {
          showError("Error", res.message ?? "Failed to allocate inventory.");
          return;
        }

        const allocateResult = parsePodOrderBatchInventoryAllocateResult(
          res.result,
        );
        const failures = getPodOrderBatchInventoryAllocateFailures(allocateResult);

        if (allocateResult && failures.length > 0) {
          const errorDetails = formatPodOrderBatchInventoryAllocateErrors(
            failures,
          );
          const { succeeded, failed, total } = allocateResult.summary;

          if (succeeded > 0) {
            showWarning(
              `${succeeded} of ${total} allocated. ${failed} failed.\n${errorDetails}`,
            );
            podOrderStore.clearSelection();
            return podOrderStore.getPodOrders();
          }

          showError(
            "Allocation failed",
            errorDetails || res.message || "Failed to allocate inventory.",
          );
          return podOrderStore.getPodOrders();
        }

        showSuccess(res.message || "Inventory allocated successfully.");
        podOrderStore.clearSelection();
        return goToTab("allocated-orders");
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          error?.message ||
          "Failed to allocate inventory.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  const handleUnassignSelected = () => {
    podOrderStore.setConfirmLoading(true);
    podOrderStore
      .batchUnassignSelected()
      .then(res => {
        if (res == null) {
          showError("Error", "No rows selected.");
          return;
        }
        if (!res.success) {
          showError("Error", res.message ?? "Failed to move orders back to Unassigned.");
          return;
        }

        const unassignResult = parsePodOrderBatchUnassignResult(res.result);
        const failures =
          unassignResult?.processed.filter(item => !item.unassigned || item.error) ??
          [];

        if (unassignResult && failures.length > 0) {
          const errorDetails = failures
            .map(item => {
              const prefix = item.order_id ? `Order ${item.order_id}: ` : "";
              return `${prefix}${item.error ?? "Could not move back to Unassigned."}`;
            })
            .join("\n");
          const { succeeded, failed, total } = unassignResult.summary;
          if (succeeded > 0) {
            showWarning(
              `${succeeded} of ${total} moved back. ${failed} failed.\n${errorDetails}`,
            );
            podOrderStore.clearSelection();
            return podOrderStore.getPodOrders();
          }
          showError(
            "Move back failed",
            errorDetails || res.message || "Failed to move orders back to Unassigned.",
          );
          return podOrderStore.getPodOrders();
        }

        showSuccess(res.message || "Orders moved back to Unassigned.");
        podOrderStore.clearSelection();
        return goToTab("unassigned");
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to move orders back to Unassigned.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  const closeWarehousePopup = () => {
    if (podOrderStore.bulkWarehouseSaving) return;
    setWarehousePopupOpen(false);
    podOrderStore.clearBulkWarehouseForm();
  };

  const handleBulkSetWarehouse = async () => {
    try {
      if (!podOrderStore.bulkWarehouseId) {
        podOrderStore.setBulkWarehouseError("Select a warehouse.");
        return;
      }
      podOrderStore.setBulkWarehouseSaving(true);
      podOrderStore.setBulkWarehouseError(null);
      const missingSir = selectedOrders.filter(
        (order) => order.stock_issue_request_id == null,
      );
      const allocatedLines = selectedOrders.filter(
        (order) => order.stock_issue_request_id != null && isAllocatedLine(order),
      );
      const stockIssueRequestIds = Array.from(
        new Set(
          selectedOrders
            .filter((order) => !isAllocatedLine(order))
            .map((order) => order.stock_issue_request_id)
            .filter((id): id is number => id != null),
        ),
      );
      const missingMessage =
        missingSir.length > 0
          ? `No stock issue request for ${missingSir
              .map(
                (order) =>
                  `Order #${order.order_id}${order.product_name?.trim() ? ` · ${order.product_name.trim()}` : ""}`,
              )
              .join(", ")}. Warehouse is not set on those lines.`
          : "";
      const allocatedMessage =
        allocatedLines.length > 0
          ? `${allocatedLines.map(describeLine).join(", ")} already allocated. Move to Unassigned before changing the warehouse.`
          : "";
      if (stockIssueRequestIds.length === 0) {
        podOrderStore.setBulkWarehouseError(
          [missingMessage, allocatedMessage].filter(Boolean).join(" "),
        );
        return;
      }
      const res = await podOrderStore.setLineWarehouse({
        stock_issue_request_ids: stockIssueRequestIds,
        warehouse_id: podOrderStore.bulkWarehouseId,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to set warehouse.");
      }
      const result = (res.result ?? {}) as {
        warehouse_id?: string;
        updated?: {
          stock_issue_request_id: number;
          order_id: number;
          available_qty?: number | null;
        }[];
        skipped?: {
          stock_issue_request_id: number;
          order_id: number;
          error: string;
        }[];
      };
      const savedWarehouseId = result.warehouse_id || podOrderStore.bulkWarehouseId;
      podOrderStore.applyLinesWarehouse(
        (result.updated ?? []).map((row) => ({
          stockIssueRequestId: row.stock_issue_request_id,
          warehouseId: savedWarehouseId,
          availableQty: row.available_qty,
          clearApproval: true,
        })),
      );
      const orderBySir = new Map(
        selectedOrders
          .filter((order) => order.stock_issue_request_id != null)
          .map((order) => [order.stock_issue_request_id as number, order]),
      );
      const skippedMessage = (result.skipped ?? [])
        .map((row) => {
          const order = orderBySir.get(row.stock_issue_request_id);
          const label = order
            ? `Order #${order.order_id}${order.product_name?.trim() ? ` · ${order.product_name.trim()}` : ""}`
            : `SIR ${row.stock_issue_request_id}`;
          return `${label}: ${row.error}`;
        })
        .join(" ");
      const notice = [missingMessage, allocatedMessage, skippedMessage]
        .filter(Boolean)
        .join(" ");
      if (notice) {
        podOrderStore.setBulkWarehouseError(notice);
        return;
      }
      setWarehousePopupOpen(false);
      podOrderStore.clearBulkWarehouseForm();
      podOrderStore.clearSelection();
    } catch (error: any) {
      podOrderStore.setBulkWarehouseError(error?.message || "Failed to set warehouse.");
    } finally {
      podOrderStore.setBulkWarehouseSaving(false);
    }
  };

  const closePrintingTypePopup = () => {
    if (printingTypeSaving) return;
    setPrintingTypePopupOpen(false);
    podOrderStore.clearPrintingTypeForm();
  };

  const handleSetPrintingType = async () => {
    if (!printingTypeValue) {
      podOrderStore.setPrintingTypeError("Select a printing type.");
      return;
    }
    const items = Array.from(
      new Map(
        selectedOrders.map((order) => [
          `${order.order_id}:${order.product_id}`,
          { order_id: order.order_id, product_id: order.product_id },
        ]),
      ).values(),
    );
    podOrderStore.setPrintingTypeSaving(true);
    podOrderStore.setPrintingTypeError(null);
    try {
      const res = await podOrderStore.setPrintingType({
        items,
        printing_type: toPodPrintingTypeFilterIdentifier(printingTypeValue),
      });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to set printing type.");
      }
      showSuccess(res.message || "Printing type saved.");
      setPrintingTypePopupOpen(false);
      podOrderStore.clearPrintingTypeForm();
      podOrderStore.clearSelection();
      await podOrderStore.getPodOrders();
    } catch (error: any) {
      podOrderStore.setPrintingTypeError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to set printing type.",
      );
    } finally {
      podOrderStore.setPrintingTypeSaving(false);
    }
  };

  const handleTabActionClick = () => {
    const action = activeTab.action;
    if (!action || selectedOrders.length === 0) return;
    if (action.targetStatus === PodOrderProgressStage.OUT_FOR_PRINTING) {
      const missingType = selectedOrders.filter(
        (order) => !order.printing_type?.trim(),
      );
      if (missingType.length > 0) {
        showError(
          "Error",
          `Printing type is not set for these items: ${missingType
            .map((order) => `#${order.order_id}${order.sku ? ` (${order.sku})` : ""}`)
            .join(", ")}. Set the printing type before print outward.`,
        );
        return;
      }
    }
    if (action.targetStatus === PodOrderProgressStage.INVENTORY_ALLOCATED) {
      handleAllocateSelected();
      return;
    }
    if (action.targetStatus === PodOrderProgressStage.OUT_FOR_PRINTING) {
      podOrderStore.openPrintOutwardDrawer().then(opened => {
        if (!opened) {
          podOrderStore.setPrintOutwardDrawerOpen(false);
          showError(
            "Error",
            podOrderStore.selectedOrders.length === 0
              ? "No rows selected."
              : "Failed to load printing files.",
          );
        }
      }).catch((error: any) => {
        podOrderStore.setPrintOutwardDrawerOpen(false);
        showError(
          "Error",
          error?.message || "Cannot move to print outward.",
        );
      });
      return;
    }
    if (action.targetStatus === PodOrderProgressStage.PARTIALLY_PRINT_INWARD) {
      if (!podOrderStore.openPrintInwardDrawer()) {
        showError("Error", "No rows selected.");
      }
    }
  };

  const handlePrintOutwardConfirm = (payload: PodOrderPrintOutwardConfirmPayload) => {
    podOrderStore.setPrintOutwardError(null);
    podOrderStore.setConfirmLoading(true);
    podOrderStore
      .batchPrintOutwardSelected(payload)
      .then(res => {
        if (res == null) {
          podOrderStore.setPrintOutwardError("No rows selected.");
          return;
        }
        if (res.success) {
          showSuccess(res.message || "Print outward submitted successfully.");
          podOrderStore.clearSelection();
          return goToTab("out-for-printing").then(() => {
            podOrderStore.setPrintOutwardDrawerOpen(false);
          });
        }
        podOrderStore.setPrintOutwardError(
          res.message ?? "Failed to submit print outward.",
        );
      })
      .catch((error: any) => {
        podOrderStore.setPrintOutwardError(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit print outward.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  const handlePrintInwardConfirm = () => {
    if (!podOrderStore.validatePrintInwardForm()) return;

    podOrderStore.setPrintInwardError(null);
    podOrderStore.setConfirmLoading(true);
    const imageAPI = new ImageAPI();

    const uploadRejectionImages = podOrderStore.printInwardLines.reduce(
      (chain, line) =>
        chain.then(() => {
          const files = podOrderStore.getPrintInwardRejectionFiles(line);
          if (files.length === 0) return;
          return imageAPI
            .uploadImage(files, "products")
            .then(uploadedUrls =>
              podOrderStore.setPrintInwardRejectionImageUrls(line, uploadedUrls),
            );
        }),
      Promise.resolve(),
    );

    uploadRejectionImages
      .then(() => podOrderStore.batchPrintInwardSelected())
      .then(res => {
        if (res == null) {
          podOrderStore.setPrintInwardError(
            "No rows selected or missing print outward id.",
          );
          return;
        }
        if (res.success) {
          showSuccess(res.message || "Print inward submitted successfully.");
          podOrderStore.clearSelection();
          return goToTab("printing-completed").then(() => {
            podOrderStore.setPrintInwardDrawerOpen(false);
          });
        }
        podOrderStore.setPrintInwardError(
          res.message ?? "Failed to submit print inward.",
        );
      })
      .catch((error: any) => {
        podOrderStore.setPrintInwardError(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit print inward.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_ALLOCATED_POD_ORDER])) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12 }}>
      <Paper elevation={0} sx={{
        borderRadius: "4px", mt: "0.6rem",
        boxShadow: "rgba(0,0,0,0.25) 0 0.0625em 0.0625em, rgba(0,0,0,0.25) 0 0.125em 0.5em, rgba(255,255,255,0.1) 0 0 0 1px inset",
      }}>
        <Box sx={{ px: 4, py: 2, minHeight: { xs: "60vh", md: window.innerHeight - 110 } }}>

          <Box sx={{
            display: "flex", justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" }, gap: 2,
          }}>
            <Box>
              <CustomTypography variant="heading3">POD Orders</CustomTypography>
              <CustomTypography variant="body2" color={theme.palette.customColors?.greyTextColor}>
                Track POD Orders through their production and shipping lifecycle.
              </CustomTypography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              {activeTab.id === "unassigned" &&
                userStore.hasPermission([
                  EmployeeRoles.UPDATE_POD_ORDER_WAREHOUSE,
                ]) && (
                <AppButton
                  variant="transparentPrimary"
                  label="Update warehouse"
                  disabled={
                    selectedOrders.length === 0 || bulkWarehouseSaving
                  }
                  onClick={() => {
                    podOrderStore.setBulkWarehouseError(null);
                    setWarehousePopupOpen(true);
                  }}
                />
              )}
              {activeTab.id === "allocated-orders" &&
                userStore.hasPermission([
                  EmployeeRoles.MANAGE_ALLOCATED_POD_PRINT,
                ]) && (
                <AppButton
                  variant="transparentPrimary"
                  label="Set printing type"
                  disabled={selectedOrders.length === 0 || printingTypeSaving}
                  onClick={() => {
                    podOrderStore.setPrintingTypeError(null);
                    setPrintingTypePopupOpen(true);
                  }}
                />
              )}
              {"secondaryAction" in activeTab &&
                activeTab.secondaryAction &&
                userStore.hasPermission([EmployeeRoles.UNASSIGN_POD_ORDER]) &&
                (activeTab.id !== "unassigned" ||
                  selectedOrders.some((order) => Number(order.approved) === 1)) && (
                <AppButton
                  variant="transparentPrimary"
                  label={activeTab.secondaryAction.label}
                  disabled={
                    selectedOrders.length === 0 ||
                    podOrderStore.confirmLoading
                  }
                  onClick={handleUnassignSelected}
                />
              )}

              {activeTab.action && (
                <Badge
                  badgeContent={selectedOrders.length}
                  invisible={selectedOrders.length === 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.7rem",
                      minWidth: 18,
                      height: 18,
                      backgroundColor: theme.palette.customColors?.white,
                      color: theme.palette.customColors?.darkBlue,
                      border: `1px solid ${theme.palette.customColors?.blackBorder}`,
                      fontWeight: 600,
                      right: 5,
                      top: 2,
                    },
                  }}
                >
                  <AppButton
                    variant="primary"
                    label={activeTab.action.label}
                    disabled={
                      (activeTab.action.targetStatus !==
                        PodOrderProgressStage.INVENTORY_ALLOCATED &&
                        !userStore.hasPermission([
                          EmployeeRoles.MANAGE_ALLOCATED_POD_PRINT,
                        ])) ||
                      selectedOrders.length === 0 ||
                      podOrderStore.confirmLoading
                    }
                    onClick={handleTabActionClick}
                  />
                </Badge>
              )}

              <AppButton
                variant="grayscale"
                label={podOrderStore.loading ? "Exporting..." : "Export"}
                disabled={
                  podOrderStore.loading ||
                  podOrderStore.podOrderListingResponse.total_count === 0
                }
                startIcon={<FileDownloadOutlined />}
                onClick={handleExport}
              />
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => podOrderStore.toggleFiltersOpen()}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: podOrderStore.filtersOpen ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {podOrderStore.filtersOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={podOrderStore.filtersOpen} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: { xs: 2, md: 1 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: "200px" }}>
                    <FormTextField
                      id="pod-order-search"
                      placeholder="Search by SKU..."
                      type="search"
                      variant="outlined"
                      value={podOrderStore.draftSearch}
                      marginBottom={0}
                      sx={{
                        mb: 0,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          minHeight: 40,
                          boxSizing: "border-box",
                        },
                      }}
                      onChange={(_, value) => podOrderStore.setDraftSearch(value ?? "")}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter") podOrderStore.applySearch();
                      }}
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={() => podOrderStore.applySearch()}
                    customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={() => podOrderStore.clearFilters()}
                    customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginTop: 2,
                    justifyContent: "space-between",
                  }}
                >

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "wrap",
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ flex: "0 0 200px", minWidth: 160, maxWidth: 260 }}>
                      <FormTextField
                        id="pod-order-customer-name"
                        placeholder="Customer name..."
                        type="text"
                        variant="outlined"
                        value={podOrderStore.draftCustomerName}
                        marginBottom={0}
                        sx={{
                          mb: 0,
                          "& .MuiOutlinedInput-root": {
                            height: 37,
                            minHeight: 37,
                            boxSizing: "border-box",
                          },
                        }}
                        onChange={(_, value) =>
                          podOrderStore.setDraftCustomerName(value ?? "")
                        }
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 180px", minWidth: 140, maxWidth: 220 }}>
                      <AppSearchDropdownMulti
                        minWidth="140px"
                        isMultiSelect={false}
                        title="Search Order"
                        fontSize="12px"
                        value={podOrderStore.draftOrderIds.map(id => ({
                          id: String(id),
                          value: String(id),
                        }))}
                        list={orderStore.orderIds.map(item => ({
                          id: String(item),
                          value: String(item),
                        }))}
                        local={false}
                        handleSearch={handleOrderSearch}
                        handleSelect={(items: FilterValue[]) => {
                          podOrderStore.setDraftOrderIds(
                            items.map(item => Number(item.id)),
                          );
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 180px", minWidth: 140, maxWidth: 220 }}>
                      <AppSearchDropdownMulti
                        minWidth="140px"
                        isMultiSelect={false}
                        title="Search Shipment"
                        fontSize="12px"
                        value={podOrderStore.draftShipmentIds.map(id => ({
                          id: String(id),
                          value: String(id),
                        }))}
                        list={podOrderStore.shipmentIdSuggestions.map(item => ({
                          id: String(item),
                          value: String(item),
                        }))}
                        local={false}
                        handleSearch={handleShipmentSearch}
                        handleSelect={(items: FilterValue[]) => {
                          podOrderStore.setDraftShipmentIds(
                            items.map(item => Number(item.id)),
                          );
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 200px", minWidth: 160, maxWidth: 260 }}>
                      <AppSearchDropdownMulti
                        minWidth="140px"
                        isMultiSelect={false}
                        title="Select Printing Type"
                        fontSize="12px"
                        value={
                          podOrderStore.draftPrintingType
                            ? [
                              {
                                id: podOrderStore.draftPrintingType,
                                value: getPodPrintingTypeLabel(
                                  podOrderStore.draftPrintingType,
                                ),
                              },
                            ]
                            : []
                        }
                        list={POD_PRINTING_TYPE_OPTIONS}
                        local={true}
                        handleSelect={(items: FilterValue[]) => {
                          podOrderStore.setDraftPrintingType(
                            items[0]?.id?.toString() ?? "",
                          );
                        }}
                      />
                    </Box>
                    {activeTab.id === "unassigned" && (
                      <Box sx={{ flex: "0 0 180px", minWidth: 150, maxWidth: 220 }}>
                        <AppSearchDropdownMulti
                          minWidth="140px"
                          isMultiSelect={false}
                          title="Previously allocated"
                          fontSize="12px"
                          value={PREVIOUSLY_ALLOCATED_OPTIONS.filter(
                            option =>
                              option.id === podOrderStore.draftPreviouslyAllocated,
                          )}
                          list={PREVIOUSLY_ALLOCATED_OPTIONS}
                          local={true}
                          handleSelect={(items: FilterValue[]) => {
                            podOrderStore.setDraftPreviouslyAllocated(
                              items[0]?.id?.toString() ?? "",
                            );
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: "0 0 220px", minWidth: 180, maxWidth: 260 }}>
                      <DateRangePickerNew
                        placeholder="Order date range"
                        startDate={
                          podOrderStore.draftOrderStartDate
                            ? dayjs(podOrderStore.draftOrderStartDate)
                            : null
                        }
                        endDate={
                          podOrderStore.draftOrderEndDate
                            ? dayjs(podOrderStore.draftOrderEndDate)
                            : null
                        }
                        onRangeSelect={range => {
                          podOrderStore.setDraftOrderDateRange(
                            range.startDate
                              ? range.startDate.format("YYYY-MM-DD")
                              : "",
                            range.endDate
                              ? range.endDate.format("YYYY-MM-DD")
                              : "",
                          );
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 220px", minWidth: 180, maxWidth: 260 }}>
                      <DateRangePickerNew
                        placeholder="Dispatch date range"
                        disableFutureDates={false}
                        startDate={
                          podOrderStore.draftDispatchStartDate
                            ? dayjs(podOrderStore.draftDispatchStartDate)
                            : null
                        }
                        endDate={
                          podOrderStore.draftDispatchEndDate
                            ? dayjs(podOrderStore.draftDispatchEndDate)
                            : null
                        }
                        onRangeSelect={range => {
                          podOrderStore.setDraftDispatchDateRange(
                            range.startDate
                              ? range.startDate.format("YYYY-MM-DD")
                              : "",
                            range.endDate
                              ? range.endDate.format("YYYY-MM-DD")
                              : "",
                          );
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={() => podOrderStore.applyFilters()}
                      customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          <Tabs
            value={activeTab.id}
            onChange={handleStatusTabChange}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={t => ({
              ...listingStatusTabsStyles(t),
              mt: 1,
              "& .MuiTab-iconWrapper": { mr: 0.5, mb: 0 },
            })}>
            {podOrderStatusTabs.map(tab => (
              <Tab
                key={tab.id}
                value={tab.id}
                id={`pod-tab-${tab.id}`}
                icon={
                  <tab.Icon
                    sx={{
                      fontSize: { xs: 16, sm: 18 },
                      color: `${tab.iconColor} !important`,
                    }}
                  />
                }
                iconPosition="start"
                label={tab.label.toUpperCase()}
              />
            ))}
          </Tabs>

          <PodAllocatedOrderListTable
            loading={podOrderStore.loading}
            orders={podOrderStore.podOrderListingResponse.list}
            selectionEnabled={activeTab.selectionEnabled}
            isRowSelected={order => podOrderStore.isOrderSelected(order)}
            sortBy={params.sort_by}
            sortType={params.sort_type}
            page={params.page ?? 1}
            limit={params.limit ?? 50}
            totalCount={podOrderStore.podOrderListingResponse.total_count}
            onSortChange={column => podOrderStore.handleSortChange(column)}
            areAllSelected={() => podOrderStore.areAllSelected()}
            areSomeSelected={() => podOrderStore.areSomeSelected()}
            onToggleSelectAll={() =>
              podOrderStore.areAllSelected()
                ? podOrderStore.clearSelection()
                : podOrderStore.selectAll()
            }
            onToggleRow={order => podOrderStore.toggleRow(order)}
            onShipmentIdClick={(shipmentId) =>
              podOrderStore.openShipmentDetailsDrawer(shipmentId)
            }
            showAvailableQty={activeTab.id === "unassigned"}
            onPageChange={(_, page) => podOrderStore.handlePageChange(page + 1)}
            onRowsPerPageChange={e =>
              podOrderStore.handleRowsPerPageChange(parseInt(e.target.value, 10))
            }
          />
        </Box>
      </Paper>

      <ShipmentDetailsDrawer
        open={selectedShipmentId !== null}
        shipmentId={selectedShipmentId}
        onClose={() => podOrderStore.closeShipmentDetailsDrawer()}
      />

      <Dialog
        open={warehousePopupOpen}
        onClose={closeWarehousePopup}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>
          Update warehouse
        </DialogTitle>
        <DialogContent>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors.greyTextColor}
          >
            Apply one warehouse to {selectedOrders.length} selected lines.
          </CustomTypography>
          {selectedOrders.some((order) => order.stock_issue_request_id == null) ? (
            <Box sx={{ mt: 1.5 }}>
              <InlineFormError
                message={`No stock issue request for ${selectedOrders
                  .filter((order) => order.stock_issue_request_id == null)
                  .map(
                    (order) =>
                      `Order #${order.order_id}${order.product_name?.trim() ? ` · ${order.product_name.trim()}` : ""}`,
                  )
                  .join(", ")}. Warehouse cannot be set on those lines.`}
              />
            </Box>
          ) : null}
          {selectedOrders.some(isAllocatedLine) ? (
            <Box sx={{ mt: 1.5 }}>
              <InlineFormError
                message={`${selectedOrders
                  .filter(isAllocatedLine)
                  .map(describeLine)
                  .join(", ")} already allocated. Move to Unassigned before changing the warehouse.`}
              />
            </Box>
          ) : null}
          <Box sx={{ mt: 2 }}>
            <AppSearchDropdownMulti
              isMultiSelect={false}
              local
              title="Warehouse"
              list={warehouseStore.warehouses.map((warehouse) => ({
                id: String(warehouse.id),
                value: warehouse.identifier || String(warehouse.id),
              }))}
              value={
                bulkWarehouseId
                  ? [
                      warehouseStore.warehouses
                        .map((warehouse) => ({
                          id: String(warehouse.id),
                          value: warehouse.identifier || String(warehouse.id),
                        }))
                        .find((option) => option.id === bulkWarehouseId) ?? {
                        id: bulkWarehouseId,
                        value: bulkWarehouseId,
                      },
                    ]
                  : []
              }
              disabled={bulkWarehouseSaving}
              fontSize="0.8rem"
              inputHeight="40px"
              minWidth="100%"
              borderColor={theme.palette.customColors.lightGrayBorder}
              handleSelect={(selectedItems: FilterValue[]) => {
                podOrderStore.setBulkWarehouseId(String(selectedItems[0]?.id ?? ""));
                podOrderStore.setBulkWarehouseError(null);
              }}
            />
          </Box>
          {bulkWarehouseError ? (
            <InlineFormError message={bulkWarehouseError} />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            variant="grayscale"
            label="Cancel"
            disabled={bulkWarehouseSaving}
            onClick={closeWarehousePopup}
          />
          <AppButton
            variant="primary"
            label={bulkWarehouseSaving ? "Saving..." : "Update"}
            disabled={bulkWarehouseSaving || !bulkWarehouseId}
            onClick={handleBulkSetWarehouse}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={printingTypePopupOpen}
        onClose={closePrintingTypePopup}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>
          Set printing type
        </DialogTitle>
        <DialogContent>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors.greyTextColor}
          >
            Apply one printing type to {selectedOrders.length} selected items.
          </CustomTypography>
          <Box sx={{ mt: 2 }}>
            <AppSearchDropdownMulti
              isMultiSelect={false}
              local
              title="Printing type"
              list={POD_PRINTING_TYPE_OPTIONS}
              value={
                printingTypeValue
                  ? [
                      {
                        id: printingTypeValue,
                        value: getPodPrintingTypeLabel(printingTypeValue),
                      },
                    ]
                  : []
              }
              disabled={printingTypeSaving}
              fontSize="0.8rem"
              inputHeight="40px"
              minWidth="100%"
              borderColor={theme.palette.customColors.lightGrayBorder}
              handleSelect={(items: FilterValue[]) => {
                podOrderStore.setPrintingTypeValue(String(items[0]?.id ?? ""));
                podOrderStore.setPrintingTypeError(null);
              }}
            />
          </Box>
          {printingTypeError ? (
            <InlineFormError message={printingTypeError} />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            variant="grayscale"
            label="Cancel"
            disabled={printingTypeSaving}
            onClick={closePrintingTypePopup}
          />
          <AppButton
            variant="primary"
            label={printingTypeSaving ? "Saving..." : "Update"}
            disabled={printingTypeSaving || !printingTypeValue}
            onClick={handleSetPrintingType}
          />
        </DialogActions>
      </Dialog>

      <PodOrderPrintOutwardDrawer
        open={podOrderStore.printOutwardDrawerOpen}
        onClose={() =>
          !podOrderStore.confirmLoading && podOrderStore.setPrintOutwardDrawerOpen(false)
        }
        submitting={podOrderStore.confirmLoading}
        onConfirm={handlePrintOutwardConfirm}
      />

      <PodOrderPrintInwardDrawer
        open={podOrderStore.printInwardDrawerOpen}
        onClose={() =>
          !podOrderStore.confirmLoading && podOrderStore.setPrintInwardDrawerOpen(false)
        }
        submitting={podOrderStore.confirmLoading}
        onConfirm={handlePrintInwardConfirm}
      />

    </Grid2>
  );
};

export default observer(PodAllocatedOrderList);
